import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Two-handle date range over the export's full history.
 *
 * A single "last N days" control can only ever anchor to today, which makes
 * "the month before the migration" unreachable. Two handles make any window
 * grabbable.
 *
 * The query only fires on release. Each change is a BigQuery scan the user
 * pays for, so firing during the drag would bill them for every intermediate
 * position.
 */

const DAY = 86400000;

export function DateRangeSlider({
  min, max, from, to, onChange,
}: {
  min: string;
  max: string;
  from: string;
  to: string;
  onChange: (from: string, to: string) => void;
}) {
  const track = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<"from" | "to" | null>(null);
  const [local, setLocal] = useState<{ from: string; to: string }>({ from, to });

  useEffect(() => { setLocal({ from, to }); }, [from, to]);

  const minT = Date.parse(min), maxT = Date.parse(max);
  const span = Math.max(1, maxT - minT);
  const pct = (iso: string) => ((Date.parse(iso) - minT) / span) * 100;

  const fromDate = useCallback((clientX: number): string => {
    const box = track.current?.getBoundingClientRect();
    if (!box) return local.from;
    const ratio = Math.min(1, Math.max(0, (clientX - box.left) / box.width));
    return isoOf(minT + Math.round((ratio * span) / DAY) * DAY);
  }, [local.from, minT, span]);

  useEffect(() => {
    if (!drag) return;
    const move = (e: PointerEvent) => {
      const d = fromDate(e.clientX);
      setLocal((s) => drag === "from"
        ? { ...s, from: d <= s.to ? d : s.to }
        : { ...s, to: d >= s.from ? d : s.from });
    };
    const up = () => setDrag(null);
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up, { once: true });
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
  }, [drag, fromDate]);

  // Commit only when the drag ends, not on every pixel.
  const committed = useRef({ from, to });
  useEffect(() => {
    if (drag) return;
    if (local.from !== committed.current.from || local.to !== committed.current.to) {
      committed.current = { from: local.from, to: local.to };
      onChange(local.from, local.to);
    }
  }, [drag, local.from, local.to, onChange]);

  function nudge(which: "from" | "to", days: number) {
    setLocal((s) => {
      const next = isoOf(Date.parse(s[which]) + days * DAY);
      const clamped = isoOf(Math.min(maxT, Math.max(minT, Date.parse(next))));
      return which === "from"
        ? { ...s, from: clamped <= s.to ? clamped : s.to }
        : { ...s, to: clamped >= s.from ? clamped : s.from };
    });
  }

  const days = Math.round((Date.parse(local.to) - Date.parse(local.from)) / DAY) + 1;

  return (
    <div className="drs">
      <div className="drs-head">
        <span className="drs-inputs">
          <input type="date" className="num" value={local.from} min={min} max={local.to}
                 aria-label="Start date"
                 onChange={(e) => e.target.value && setLocal((s) => ({ ...s, from: e.target.value }))} />
          <span aria-hidden="true">→</span>
          <input type="date" className="num" value={local.to} min={local.from} max={max}
                 aria-label="End date"
                 onChange={(e) => e.target.value && setLocal((s) => ({ ...s, to: e.target.value }))} />
        </span>
        <span className="drs-days">{days.toLocaleString()} days</span>
      </div>

      <div className="drs-track" ref={track}>
        <div className="drs-rail" />
        <div className="drs-fill"
             style={{ left: `${pct(local.from)}%`,
                      width: `${Math.max(0, pct(local.to) - pct(local.from))}%` }} />
        {(["from", "to"] as const).map((which) => (
          <button
            key={which}
            className={`drs-handle ${drag === which ? "on" : ""}`}
            style={{ left: `${pct(local[which])}%` }}
            role="slider"
            aria-label={which === "from" ? "Start date" : "End date"}
            aria-valuemin={minT} aria-valuemax={maxT}
            aria-valuenow={Date.parse(local[which])}
            aria-valuetext={local[which]}
            onPointerDown={(e) => { e.preventDefault(); setDrag(which); }}
            onKeyDown={(e) => {
              const step = e.shiftKey ? 7 : 1;
              if (e.key === "ArrowLeft") { e.preventDefault(); nudge(which, -step); }
              if (e.key === "ArrowRight") { e.preventDefault(); nudge(which, step); }
            }}
          />
        ))}
      </div>

      <div className="drs-ticks">
        {monthTicks(minT, maxT).map((t) => (
          <span key={t.t} style={{ left: `${((t.t - minT) / span) * 100}%` }}>{t.label}</span>
        ))}
      </div>

      <div className="drs-presets">
        {[
          { label: "7d", days: 7 }, { label: "28d", days: 28 },
          { label: "3m", days: 90 }, { label: "6m", days: 182 },
          { label: "12m", days: 365 },
        ].map((p) => (
          <button key={p.label}
                  className={days === p.days ? "on" : ""}
                  onClick={() => {
                    const t = isoOf(maxT);
                    const f = isoOf(Math.max(minT, maxT - (p.days - 1) * DAY));
                    setLocal({ from: f, to: t });
                  }}>
            {p.label}
          </button>
        ))}
        <button className={local.from === min && local.to === max ? "on" : ""}
                onClick={() => setLocal({ from: min, to: max })}>
          All
        </button>
      </div>
    </div>
  );
}

const isoOf = (t: number) => new Date(t).toISOString().slice(0, 10);

/** Month starts, thinned so labels never collide on a short history. */
function monthTicks(minT: number, maxT: number): { t: number; label: string }[] {
  const out: { t: number; label: string }[] = [];
  const d = new Date(minT);
  d.setUTCDate(1);
  if (d.getTime() < minT) d.setUTCMonth(d.getUTCMonth() + 1);
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  while (d.getTime() <= maxT) {
    out.push({
      t: d.getTime(),
      label: d.getUTCMonth() === 0
        ? String(d.getUTCFullYear())
        : months[d.getUTCMonth()],
    });
    d.setUTCMonth(d.getUTCMonth() + 1);
  }
  const stride = Math.ceil(out.length / 10);
  return out.filter((_, i) => i % stride === 0);
}
